/**
 * Serbian overrides for the admin screens.
 *
 * Bosnian moved from ijekavica to ekavica — *uspjela* to *uspela*, *mjesec*
 * to *mesec*, *prijedlog* to *predlog*. Where the two standards agree there
 * is no entry, which is why this file is short and should stay that way.
 *
 * Latin script, matching the rest of the application.
 *
 * The stems that actually occur on these screens: *izmjena* → *izmena*,
 * *ocjena* → *ocena*, *mijenjati* → *menjati*, *umjesto* → *umesto*,
 * *obavještenje* → *obaveštenje*, *izvještaj* → *izveštaj*, *naljepnica* →
 * *nalepnica*, *redoslijed* → *redosled*, *sljedeći* → *sledeći*, *gdje* →
 * *gde*, *uvijek* → *uvek*. Two that are not jat at all but move with the
 * standard anyway: *vrijedi* → *važi*, and *korišten* → *korišćen*.
 */

import type { AdminSlice } from '../bs/admin'

export const admin: Partial<AdminSlice> = {
  // *sljedeće* → *sledeće*. The four count labels and the clean-run sentence
  // have no jat in them and are inherited.
  'admin.mes.sync.problems':
    'Sinhronizacija je prošla, ali treba obratiti pažnju na sledeće ({count}):',
  // *smer*, *menjaju*, *izmena*.
  'admin.mes.help':
    'Podaci idu samo u jednom smeru: KaproMES je vlasnik pogona, radnih centara i ljudi, pa polja koja on drži ovde ne mogu da se menjaju — izmena bi ostala do noćne sinhronizacije i onda nestala. Sinhronizacija ništa ne briše: radni centar ili osoba koja prestane da se pojavljuje u odgovoru se deaktivira, jer je izostanak iz odgovora mnogo češće greška u vezi ili filter nego mašina koja je zaista otpisana. Preuzeta osoba ne može da se prijavi dok sama ne postavi lozinku — lozinke se ne preuzimaju i ne tražimo ih.',
  // *sledećem*, *ovde*, *predlozima*.
  'admin.mes.roles.explain':
    'Preko sinhronizacije prelazi samo administratorska uloga i samo naviše: ko je administrator u KaproMES-u postaje administrator i ovde pri sledećem preuzimanju, a gubitak te uloge tamo je ne oduzima ovde — uloge koje odlučuju o predlozima i nagradama KaproMES ne poznaje, pa bi ih spuštanje svake noći brisalo.',
  // *izveštaj*, *posle*.
  'admin.mes.report.explain':
    'Izveštaj posle preuzimanja nabraja i podignute administratore i uloge iz KaproMES-a koje ne prepoznajemo — takav nalog nastavlja da se sinhronizuje, samo mu se uloga ne dira.',

  // ---- Shell -------------------------------------------------------
  'admin.subtitle': 'Korisnici, šifarnici, 5S obrasci i dnevnik izmena',
  'admin.nav.badges': 'QR nalepnice',

  // ---- Shared verbs and fields -------------------------------------
  'admin.action.edit.label': 'Izmeni {name}',
  'admin.form.editTitle': 'Izmena — {name}',
  'admin.saved': 'Izmene su sačuvane.',
  'admin.delete.failed': 'Brisanje nije uspelo.',
  'admin.field.sortOrder': 'Redosled',

  // ---- Users -------------------------------------------------------
  // `admin.users.help` is not an entry: the whole sentence is about codes and
  // activation and carries no jat at all, so the Bosnian is already Serbian.
  'admin.users.empty.description': 'Promenite pretragu ili dodajte novog.',
  'admin.users.action.edit.tip': 'Izmeni podatke',
  'admin.users.delete.confirm':
    'Obrisati korisnika „{name}"?\n\nNjegove prijave, ocene i evaluacije ostaju — one su istorija pogona — ali više ne pokazuju ko ih je podneo. Ako treba da se zna, umesto brisanja isključite „Nalog je aktivan": pristup gubi isto tako odmah, a ime ostaje.',

  // *dve*, *deli*, *promenite*.
  'admin.users.field.code.explain':
    'Mora biti jedinstvena — dve osobe ne mogu da je dele; ako je kasnije promenite, korisnik se od tada prijavljuje novom šifrom, a ona odštampana na njegovoj kartici više ne odgovara.',
  // *ocenjuju*, *predloge*, *dodeljuju*, *ovde*.
  'admin.users.role.explain':
    'Uloge koje pregledaju tuđe prijave, ocenjuju predloge i dodeljuju nagrade postoje samo u KaProLean-u — KaproMES ne zna ko odobrava poboljšanje, pa se te uloge dodeljuju ovde i sinhronizacija ih ne dira.',

  'admin.users.field.notify': 'Šalji obaveštenja i na e-poštu',
  'admin.users.field.notify.hint':
    'Obaveštenja u aplikaciji stižu uvek. Ovo važi samo ako korisnik ima e-poštu.',

  'admin.users.reset.note':
    'Ne birate lozinku umesto njih — dobijate kod za jednokratnu prijavu, a lozinku postavlja radnik.',
  // *vrijedi* → *važi*: not a jat reflex, but it moves with the standard and
  // the marker list in i18n.test.ts catches it either way.
  'admin.users.reset.explain':
    'Kod važi za jednu prijavu i nalog do tada ne može ništa drugo — korisnik se njime prijavi i odmah postavi svoju lozinku.',
  'admin.users.issue.failed': 'Izdavanje nije uspelo.',

  'admin.users.badge.why':
    'Radnik skenira karticu umesto da kuca šifru i lozinku — brže kad se stoji pored mašine, sa rukavicama.',
  'admin.users.badge.revoke.failed': 'Poništavanje nije uspelo.',

  // ---- Work centres ------------------------------------------------
  // `delete.confirm` and `field.type.hint` are no longer entries here: the
  // Bosnian lost the stale sentence in one and the customer-specific example in
  // the other, and what is left of both reads the same in ekavica.
  'admin.workCenters.created': 'Radni centar je kreiran. Odštampajte QR nalepnicu.',
  'admin.workCenters.noTemplates':
    'Novi radni centar ne može da se kreira dok ne postoji aktivan 5S obrazac za njegov tip — tip se bira iz liste obrazaca, a nijedan još nije napravljen. Otvorite „5S obrasci" i napravite prvi.',
  'admin.workCenters.field.code.hint':
    'Velika slova, brojevi, crtica. Koristi se u URL-ovima i na nalepnici.',
  'admin.workCenters.field.type.explain':
    'Isti obrazac može da koristi više radnih centara, a izmena obrasca važi za sve njih. Ako ovom mestu treba sopstvena lista kriterijuma, napravite mu novi obrazac u „5S obrasci" i izaberite ga ovde.',
  'admin.workCenters.field.type.hint':
    'Radni centar se ocenjuje po obrascu koji ovde izaberete. Ako obrasca nema u listi, prvo ga napravite u „5S obrasci".',
  'admin.workCenters.field.type.orphan':
    'Tip „{type}" nema aktivan 5S obrazac, pa ovaj radni centar trenutno ne može da se ocenjuje.',
  'admin.workCenters.field.type.ownForm':
    'Ovaj radni centar ima sopstveni obrazac „{name}" i ocenjuje se po njemu, bez obzira na izbor ovde.',
  'admin.workCenters.noForm.explain':
    'Ovaj radni centar ne može da se 5S ocenjuje jer za tip „{type}" ne postoji aktivan obrazac. Napravite obrazac sa tim tipom u „5S obrasci".',
  'admin.workCenters.noQr.explain':
    'Mašina još nema QR kod, pa radnik ne može da je skenira — može samo da je izabere iz liste. Kod se pravi dugmetom „Obnovi" u „QR nalepnice", a tamo se i štampa.',
  'admin.templates.noCentres': 'Nijedan radni centar se još ne ocenjuje po ovom obrascu.',
  'admin.templates.addCentre.hint':
    'Otvara formu za novi radni centar sa ovim obrascem već izabranim. Postojeći radni centar prebacujete na ovaj obrazac tako što ga uredite u „Radni centri" — osim radnih centara preuzetih iz KaproMES-a, čiji se tip menja u KaproMES-u.',
  'admin.templates.field.type.explain':
    'Tip povezuje obrazac sa radnim centrima: svaki radni centar ovog tipa ocenjuje se po ovom obrascu. Tip ne morate da pamtite — kad pravite radni centar, obrazac birate po nazivu.',
  'admin.workCenters.field.description.placeholder':
    'Neobavezno — čemu centar služi, gde se nalazi u pogonu.',

  // ---- Plants ------------------------------------------------------
  // *pre*, *ocenjeno*.
  'admin.plants.help':
    'Hijerarhija je firma → pogon → radni centar: svaka prijava i svaka 5S evaluacija visi o radnom centru, a svaki radni centar o tačno jednom pogonu. Zato pogon postoji pre radnih centara koje drži. Brisanje pogona nije uklanjanje jedne lokacije — sa njim trajno odlaze i svi radni centri u njemu, zajedno sa svime što je na njima prijavljeno i ocenjeno.',
  'admin.plants.updated': 'Pogon je izmenjen.',
  'admin.plants.field.code.hint':
    'Velika slova i brojevi. Pojavljuje se u izveštajima i na nalepnicama.',

  // ---- 5S templates ------------------------------------------------
  'admin.templates.help':
    'Obrasci su vaši — nova radna mesta i nove kriterijume unosite sami, bez nas. Redosled nije proizvoljan: prvo obrazac (naziv, tip radnog centra i skala), zatim pet kategorija, pa kriterijumi u njima, i tek onda radni centar tog tipa. Radni centar ne može da se sačuva dok njegov tip nema aktivan obrazac.',
  'admin.templates.newVersion.hint':
    'Pravi kopiju obrasca kao sledeću verziju i arhivira ovu — ranije evaluacije ostaju vezane za nju i njihove ocene se ne menjaju, a nove se rade po novoj verziji.',
  'admin.templates.locked.noAdd':
    'Kategorije i kriterijumi ne mogu da se dodaju jer su ranije evaluacije ocenjene po ovom obrascu. Kategoriju po kojoj niko nikada nije ocenjivan možete obrisati — nema ocene koju bi to promenilo. Sve ostalo ide kroz „Nova verzija" na vrhu kartice.',
  'admin.templates.criterion.remove.hint':
    'Kriterijum po kojem je već ocenjivano ne briše se nego se deaktivira — ostaje ovde precrtan da bi ranije evaluacije i dalje bile čitljive.',
  'admin.templates.gaps.help':
    'Radnik koji stane pred ove mašine ne može da uradi 5S obilazak: njihov tip nema aktivan obrazac, pa aplikacija nema šta da mu prikaže. Označite tipove, izaberite postojeći obrazac koji im najviše odgovara i primenite ga — ili napravite novi obrazac za svaki tip.',
  'admin.templates.field.scale.explain':
    'Skala 1–5 daje ocenjivaču pet stepeni i finiju sliku stanja, a 1–3 se brže popunjava na terenu; broj se zaključava nakon prve predate evaluacije, jer je svaki već izračunat procenat računat po njemu.',
  'admin.templates.lockedNote':
    'Obrazac je korišćen za predate evaluacije, pa se tekst kriterijuma i skala više ne mogu menjati — izmena bi retroaktivno promenila značenje ranijih ocena. Da biste ga menjali, napravite novu verziju.',
  'admin.templates.newVersion.confirm':
    'Napraviti verziju {next} obrasca „{name}"?\n\nVerzija {current} se arhivira, ali ranije evaluacije ostaju vezane za nju i njihove ocene se ne menjaju. Nove evaluacije koriste novu verziju.',
  'admin.templates.newVersion.failed': 'Kreiranje verzije nije uspelo.',
  'admin.templates.archived.note':
    'Ranije evaluacije su vezane za ove verzije i njihove ocene ostaju nepromenjene.',

  'admin.templates.criterion.edit': 'Izmeni',
  'admin.templates.criterion.edit.label': 'Izmeni: {criterion}',
  'admin.templates.criterion.editTitle': 'Izmena kriterijuma',
  'admin.templates.criterion.hint':
    'Tekst koji ocenjivač čita na terenu — treba da odgovara štampanom obrascu.',
  'admin.templates.criterion.placeholder':
    'npr. Nema nepotrebne opreme, alata i materijala na radnom mestu',

  'admin.templates.guidance.hint':
    'Neobavezna pomoć ocenjivaču. Može se menjati i na zaključanom obrascu.',
  'admin.templates.guidance.placeholder': 'npr. Proveriti i ispod stola i iza mašine.',

  'admin.templates.category.failed': 'Dodavanje nije uspelo.',
  'admin.templates.category.delete.warning':
    'Briše se zajedno sa kriterijumima u njoj ({count}). Ako je po nekom od njih već ocenjivano, brisanje se odbija — te ocene bi ostale bez kriterijuma koji objašnjava šta znače.',

  /*
   * A checklist written for one machine.
   *
   * Serbian and Bosnian share every word of this except the jat, so every
   * override below is one: *ocjenjuje* → *ocenjuje*, *mijenjaju* → *menjaju*,
   * *nepromijenjeni* → *nepromenjeni*, *umjesto* → *umesto*, *Bilješka* →
   * *Beleška*, *primjenjuje* → *primenjuje*. Three of those the marker list in
   * i18n.test.ts does not know about, which is the usual reason a Serbian file
   * ends up quietly still Bosnian.
   */
  'admin.templates.settings': 'Izmeni obrazac',
  'admin.templates.machine.carveOut.hint':
    'Pravi kopiju ovog obrasca samo za jednu mašinu — original i ostale mašine istog tipa ostaju nepromenjeni, a kopiju odmah možete urediti.',
  'admin.templates.machine.carveOut.note':
    'Mašina se od sada ocenjuje po svojoj kopiji, a ne po obrascu tipa. Ostale mašine tog tipa se ne menjaju. Kad god poželite, možete je vratiti na obrazac tipa.',
  'admin.templates.machine.field.placeholder': 'Izaberite radni centar…',
  'admin.templates.machine.detach.hint':
    'Mašina se ponovo ocenjuje po obrascu svog tipa; ako je po ovoj kopiji već ocenjivano, ona se arhivira umesto da se obriše, da ranije evaluacije ostanu čitljive.',

  'admin.templates.field.type.hint':
    'Obrazac se primenjuje na sve radne centre ovog tipa. Ponuđeni su tipovi koje vaše mašine već koriste; možete upisati i tip koji još ne postoji.',
  'admin.templates.field.type.hint.empty':
    'Obrazac se primenjuje na sve radne centre ovog tipa. Upišite tip onako kako ga zovete u pogonu.',
  'admin.templates.field.scale.locked':
    'Skala ne može da se menja jer su po ovom obrascu već predate evaluacije — svaki izračunat procenat je računat po njoj. Napravite novu verziju dugmetom „Nova verzija" na vrhu kartice i u njoj upišite novu skalu.',
  'admin.templates.field.notes.hint':
    'Beleška za administratore — ocenjivač je ne vidi na obrascu.',

  // ---- Badge sheet -------------------------------------------------
  'admin.badgeSheet.intro':
    'Izaberite radnike. Svako dobija svoju karticu koju skenira umesto da kuca šifru i lozinku.',
  'admin.badgeSheet.printHint':
    'Odštampajte na A4, isecite po isprekidanoj liniji i plastificirajte. Kartice se prikazuju samo sada — kad zatvorite prozor, nema ih više.',
  'admin.badgeSheet.failed': 'Izdavanje nije uspelo do kraja.',

  // ---- Activation codes for a whole shift --------------------------
  // Three lines, and no more: *isjecite* → *isecite*, *poslije/prije* → *posle/
  // pre* with *nigdje* → *nigde* beside them, and *vrijedi* → *važi*. The rest
  // of the block — the intro, both empty-state lines, the reissue warning and
  // the sync sentence — has no jat in it and is inherited.
  'admin.activation.printHint':
    'Odštampajte na A4, isecite po isprekidanoj liniji i predajte svakom njegov listić. Kodovi se prikazuju samo sada — kad napustite ovaj ekran, nema ih više.',
  'admin.activation.warning':
    'Kodovi se čuvaju šifrovani, pa se posle ovoga ne mogu nigde pročitati. Odštampajte ih ili prepišite pre nego napustite ekran; ako se izgube, izdajte nove.',
  'admin.activation.slip.instructions':
    'Prijavite se šifrom zaposlenog i ovim kodom, a zatim postavite svoju lozinku — kod posle toga više ne važi.',

  // ---- QR stickers on the machines ---------------------------------
  // *nalepnica*, *nigde*, *sme*, *zalepite*.
  'admin.stickers.help':
    'Obnavljanje koda poništava svaku nalepnicu koja je već na toj mašini: stari kod od tog trenutka ne vodi nigde, pa radnik koji ga skenira dobija poruku da kod nije poznat i mora sam da bira radni centar. Obnovite kod tek kad je nalepnica oštećena ili kad stari kod ne sme više da bude u opticaju, i odmah odštampajte i zalepite novu.',

  // ---- Activity log ------------------------------------------------
  // *beleži*, *izmene*, *vrednost*, *promenjen*, *video*.
  'admin.log.help':
    'Dnevnik sam beleži izmene na prijavama i evaluacijama, polje po polje, sa starom i novom vrednošću; uz to administratorski ekrani upisuju i ono što nije izmena zapisa — izdat aktivacioni kod, izdatu ili poništenu karticu, novu verziju obrasca, obnovljen QR kod, promenjenu valutu. Čitanje se ne beleži: dnevnik odgovara na pitanje ko je šta promenio, a ne ko je šta video.',
  'admin.log.filter.subject': 'Šta je menjano',
  'admin.log.filter.event': 'Vrsta izmene',
  'admin.log.filter.event.all': 'Sve izmene',
  'admin.log.filter.causer': 'Ko je menjao',
  'admin.log.empty.description':
    'Dnevnik se puni kada korisnici menjaju prijave, evaluacije i šifarnike.',
  'admin.log.change.arrow': 'promenjeno u',
  'admin.log.pager.next': 'Sledeća stranica',

  // Rows the MES sync owns. See SyncedFromBadge for why the reason is
  // spelled out rather than the field merely being greyed.
  'admin.synced.tooltip': 'Ovaj zapis održava {system}. Izmene se rade tamo.',
  'admin.synced.lastSyncedAt': 'Poslednja sinhronizacija: {at}',
  'admin.synced.fieldLocked': 'Održava {system} — sledeća sinhronizacija bi vratila izmenu.',

  // The defect list above the templates — see pages/admin/TemplateGaps.tsx.
  'admin.templates.gaps.source': 'Obrazac koji se primenjuje',
  'admin.templates.gaps.apply': 'Primeni na {count} tipova',
  'admin.templates.gaps.note': 'Kopija je zaseban obrazac — original ostaje aktivan i nepromenjen. Kriterijume možete urediti posle.',
  'admin.workCenters.delete.warning': 'Trajno se brišu i sve prijave, 5S evaluacije i prilozi vezani za ovaj radni centar. Ovo ne može da se vrati.',
  'admin.templates.new.done': 'Obrazac je kreiran. Sada dodajte kategorije i kriterijume.',
  'admin.templates.field.scale': 'Maksimalna ocena',
  'admin.templates.field.scale.hint': 'Obično 5 — toliko stepeni ocene ima svaki kriterijum.',

  // Ekavica for the five new settings strings. Every one of them starts life as
  // a copy of the Bosnian, and four carry a jat the marker list did not catch —
  // see the note beside `promjen` in i18n.test.ts.
  'admin.settings.help':
    'Valuta je jedna za celu firmu: menja oznaku uz svaki iznos — na ekranu, u PDF izveštaju, u tabeli i u obaveštenjima. Brojevi se pri tome ne preračunavaju: 500 ostaje 500 i samo dobija novu oznaku, jer aplikacija nema kurs, a izmišljen kurs bio bi gori od promene oznake.',
  'admin.settings.currency.explain':
    'Promena se upisuje u dnevnik izmena sa starom i novom valutom, pa se kasnije zna od kada su iznosi u drugoj valuti.',
  'admin.settings.currency.hint':
    'U čemu firma računa uštede, troškove i nagrade. Vredi za sve — za ekran, za PDF, za tabelu i za obaveštenja.',
  'admin.settings.currency.warning.one':
    'Već je upisan {count} iznos u trenutnoj valuti. Nakon promene će se prikazivati u novoj.',
  'admin.settings.currency.warning.few':
    'Već su upisana {count} iznosa u trenutnoj valuti. Nakon promene će se prikazivati u novoj.',
  'admin.settings.currency.warning.many':
    'Već je upisano {count} iznosa u trenutnoj valuti. Nakon promene će se prikazivati u novoj.',
  'admin.settings.currency.warningEmpty':
    'Za sada nema upisanih iznosa, pa je ovo najbolji trenutak za promenu.',
  'admin.settings.currency.noConversion':
    'Brojevi se ne preračunavaju — menja se samo oznaka valute pored njih.',
  'admin.settings.currency.saved': 'Valuta je promenjena.',

  // *smjena* -> *smena* throughout, plus *definisati*, *prije* -> *pre*,
  // *izmijenjena* -> *izmenjena*, *vrijeme* -> *vreme*, *nigdje* -> *nigde*.
  'admin.nav.shifts': 'Smene',
  'admin.shifts.subtitle': 'Smene u kojima se radi.',
  'admin.shifts.help':
    'Smene su neobavezne: ako ih ne unesete, polje za smenu se uopšte ne prikazuje na 5S obrascu. Unesite ih ako želite da poredite kako se radni centri održavaju iz smene u smenu — evaluator tada bira smenu pri svakom obilasku. Noćna smena se završava pre nego što počne i tako se i upisuje.',
  'admin.shifts.new': 'Nova smena',
  'admin.shifts.empty.title': 'Nema definisanih smena',
  'admin.shifts.empty.description':
    'Dodajte prvu smenu, ili ostavite prazno ako se u pogonu radi u jednoj smeni.',
  'admin.shifts.created': 'Smena je kreirana.',
  'admin.shifts.updated': 'Smena je izmenjena.',
  'admin.shifts.form.description': 'Naziv je obavezan; vreme nije.',
  'admin.shifts.field.window.hint': 'Noćna smena se završava pre nego počne — to je u redu.',
  'admin.shifts.field.window.explain':
    'Vreme služi samo da obrazac sam ponudi smenu koja traje u trenutku obilaska; evaluator tu ponudu menja jednim dodirom, a smena bez vremena se bira ručno.',
  'admin.shifts.field.active.hint':
    'Neaktivna smena se ne nudi na obrascu, ali ostaje na ranijim evaluacijama.',
  'admin.shifts.delete.confirm': 'Obrisati smenu „{name}"?',
  'admin.shifts.delete.willDelete': 'Smena se nigde ne koristi, pa se briše trajno.',
  'admin.shifts.delete.willDeactivate':
    'Smena se koristi u {count} evaluacija, pa se neće obrisati nego deaktivirati. Ranije evaluacije zadržavaju njen naziv.',

  // ---- Tooltips across Administracija ----
  // Only the strings with a jat reflex in them, plus *vlastiti* → *sopstveni*
  // and *da* plus a present tense where Serbian prefers it. The toolbar, the
  // sign-in column, the delete body, the badge note on a reset, the two plant
  // counts, "Sistem" and the lost slip carry no jat and are inherited.
  'admin.users.column.role.explain':
    'Kod deaktiviranog naloga ovde piše „Neaktivan" umesto uloge. Uloga ostaje sačuvana i vraća se čim nalog ponovo aktivirate.',
  'admin.users.field.plant.explain':
    'Pogon je samo oznaka gde je osoba zavedena — vidi se na njenom profilu i u broju korisnika na „Pogoni", ali ne određuje na kojim radnim centrima radi. Sinhronizacija iz KaproMES-a upisuje ga samo kad je prazan.',
  'admin.users.field.email.explain':
    'Obaveštenja stižu na nju samo ako je uključeno „Šalji obaveštenja i na e-poštu". Zaboravljenu lozinku korisnik može sam da vrati preko e-pošte tek kad adresu potvrdi na svom profilu.',
  'admin.users.field.active.synced':
    'Ovaj nalog održava KaproMES i svaka sinhronizacija mu postavlja aktivnost prema statusu u KaproMES-u — ako je osoba tamo i dalje aktivna, deaktivacija ovde traje samo do sledeće sinhronizacije.',
  'admin.users.badge.revoke.explain':
    '„Poništi" odmah gasi karticu i ne izdaje novu — radnik se dalje prijavljuje šifrom i lozinkom. Za zamenu izgubljene kartice koristite „Izdaj novu karticu".',
  'admin.users.badge.closeUnprinted':
    'Zatvorite li prozor pre štampe, kartica ostaje izdata, ali više ne može da se prikaže — tada izdajte novu.',

  'admin.plants.field.name.explain':
    'Naziv pogona štampa se na QR nalepnicama njegovih radnih centara. Nalepnice koje su već zalepljene rade i nakon promene, samo nose stari naziv.',

  'admin.shifts.subtitle.explain':
    'Sa definisanim smenama isti radni centar ima jednu 5S evaluaciju dnevno za svaku smenu; bez smena — jednu dnevno.',
  'admin.shifts.field.name.explain':
    'Ovaj naziv evaluator bira na 5S obrascu i njime su označeni grafikoni po smenama. Preimenovanje se vidi i na ranijim evaluacijama — naziv se ne pamti posebno uz svaku.',
  'admin.shifts.field.endsAt.explain':
    'Upišite oba vremena ili nijedno — samo početak ili samo kraj ne može da se sačuva. Isti početak i kraj ne pokrivaju nijedan trenutak, pa takvu smenu obrazac nikad ne ponudi sam.',
  'admin.shifts.field.sortOrder.explain':
    'Tim redom se smene nude na 5S obrascu i ređaju na grafikonima. Ako se vremena dve smene preklapaju, obrazac sam ponudi onu koja je ovde prva.',

  'admin.settings.currency.recorded.explain':
    'Broji se svaki predlog sa upisanom očekivanom ili stvarnom uštedom ili troškom — jednom, koliko god polja imao — i svaka dodeljena nagrada.',
  'admin.settings.currency.undo.explain':
    'Zato promena može i da se vrati: izaberite staru valutu i iznosi opet dobiju staru oznaku. Isto se tada dešava i iznosima upisanim u međuvremenu, iako su upisani u novoj.',

  'admin.mes.active.explain':
    'Sinhronizacija postavlja i aktivnost. Radnik je aktivan dok je aktivan u KaproMES-u — deaktivacija ovde traje samo do sledeće sinhronizacije. Radni centar iz KaproMES-a ovde ne može ni da se deaktivira: aktivan je dok ga KaproMES vraća.',
  'admin.mes.save.explain':
    '„Testiraj vezu" i „Sinhronizuj sada" rade sa sačuvanim podacima. Nakon izmene adrese ili tokena prvo pritisnite „Sačuvaj", pa tek onda testirajte.',

  'admin.templates.gaps.body.explain':
    'Broje se samo aktivni radni centri. Mašina koja ima sopstveni obrazac se ne broji, iako njen tip nema obrazac.',
  'admin.templates.gaps.source.explain':
    'Broj u zagradi je broj kriterijuma koji se kopiraju. Kopija dobija istu skalu ocena i naziv „5S — tip", koji posle možete promeniti.',

  'admin.log.search.explain':
    'Pretraga gleda samo tekst zapisa, a on je uvek na bosanskom — npr. šifra zaposlenog ili „Obrisan". Izmene prijava i evaluacija tako se ne nalaze; za njih koristite filtere ispod.',
  'admin.log.filter.event.explain':
    'Vrstu izmene imaju samo automatski zapisi o prijavama i 5S evaluacijama. Čim je izaberete, nestaju zapisi administratorskih ekrana — izdati kodovi, kartice, obrisani korisnici i slično.',
  'admin.log.filter.logName.explain':
    'Deo aplikacije koji je upisao zapis, pod internim nazivom: user su korisnici, work_center radni centri, five_s 5S evaluacije, submission prijave, mes sinhronizacija sa KaproMES-om.',
  'admin.log.impersonated.explain':
    'Izmenu je napravila podrška KaProLean-a prijavljena kao ovaj korisnik: ime ispred je nalog koji je korišćen, a ovde piše ko je zaista radio.',

  'admin.badgeSheet.intro.explain':
    'Na spisku su samo radnici — karticu za nekog drugog izdajete pojedinačno iz liste korisnika. Unapred su označeni aktivni radnici koji još nemaju karticu.',
  'admin.badgeSheet.closeUnprinted':
    'Zatvorite li prozor pre štampe, kartice ostaju izdate, ali više ne mogu da se prikažu. Tada ih izdajte ponovo — ovde ih označite ručno, jer ih spisak više ne označava sam.',
  'admin.activation.intro.explain':
    'Na spisku su aktivni nalozi bez sopstvene lozinke: novi, preuzeti iz KaproMES-a i oni kojima je pristup vraćen kodom. Ko je već postavio svoju lozinku, ne dobija kod i lozinka mu ostaje.',
}
